import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { farmScopeQuery } from '@/stores/farmScope';

/**
 * Data hooks for Sales & Dispatch.
 *
 * Shared rather than copied into each screen, which is the lesson the inventory
 * hooks file records: the SKU picker ended up capped at 200 items in four places
 * at once before they were pulled together.
 *
 * Where a route has an `@ApiOkResponse` the generated schema type is used. Where it
 * has none the typed client can only say `unknown`, so the shape is declared here
 * and asserted at that one boundary — rather than scattering `any` through the
 * screens that consume it.
 */

export type DispatchRow = components['schemas']['DispatchRowDto'];
export type DispatchDetail = components['schemas']['DispatchDetailDto'];
export type BirdsAlive = components['schemas']['BirdsAliveDto'];
export type NextBookNo = components['schemas']['NextBookNoDto'];
export type Farm = components['schemas']['FarmDto'];

export interface BuyerRow {
  id: string;
  code: string;
  kind: 'BROKER' | 'CUSTOMER' | 'WALK_IN';
  nameEn: string;
  nameUr: string | null;
  contactNo: string | null;
  cnic: string | null;
  address: string | null;
  creditLimit: string | null;
  creditDays: number | null;
  isActive: boolean;
  version: number;
  closingBalance: string;
  overLimit: boolean;
}

export interface LiveFlock {
  id: string;
  code: string;
  nameEn: string;
  farmId: string;
  birdsAlive: number;
}

export interface OtherSaleRow {
  id: string;
  documentNo: string;
  status: string;
  kind: 'EMPTY_BAG' | 'MANURE' | 'SCRAP_OTHER';
  unit: string;
  soldOn: string;
  farmId: string;
  farmName: string;
  storeId: string | null;
  storeName: string | null;
  buyerId: string;
  buyerName: string;
  qty: string;
  rate: string;
  amount: string;
  paidInCash: boolean;
  intoCashBoxId: string | null;
  remarks: string | null;
  reversalReason: string | null;
  version: number;
}

export interface Paged<T> {
  items: T[];
  total: number;
}

export interface CashBoxRow {
  id: string;
  code: string;
  nameEn: string;
  farmId: string | null;
  farmName: string;
  isActive: boolean;
  closingBalance: string;
  version: number;
}

/** Everything a posted or reversed dispatch changes. */
export function useSalesInvalidation() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sd'] });
    // A dispatch moves a flock's birds alive and its sold figures, and a bill is
    // raised from it, so neither of those caches can be left as it was.
    void queryClient.invalidateQueries({ queryKey: ['flocks'] });
    void queryClient.invalidateQueries({ queryKey: ['fin'] });
  }, [queryClient]);
}

export interface DispatchFilters {
  farmIds: readonly string[];
  flockId?: string;
  buyerId?: string;
  stage?: string;
  from?: string;
  to?: string;
  search?: string;
  take: number;
  skip: number;
}

export function useDispatches(filters: DispatchFilters) {
  return useQuery({
    queryKey: ['sd', 'dispatches', filters],
    queryFn: () =>
      api.get('/api/v1/sd/dispatches', {
        ...farmScopeQuery(filters.farmIds),
        ...(filters.flockId ? { flockId: filters.flockId } : {}),
        ...(filters.buyerId ? { buyerId: filters.buyerId } : {}),
        ...(filters.stage && filters.stage !== 'all' ? { stage: filters.stage } : {}),
        ...(filters.from ? { from: filters.from } : {}),
        ...(filters.to ? { to: filters.to } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        take: filters.take,
        skip: filters.skip,
      }),
  });
}

export function useDispatch(id: string | null) {
  return useQuery({
    queryKey: ['sd', 'dispatch', id],
    queryFn: () => api.get(`/api/v1/sd/dispatches/{id}`.replace('{id}', id!) as never),
    enabled: Boolean(id),
  });
}

/**
 * Birds still in a flock.
 *
 * Kept fresh rather than cached: it is shown beside the bird count as a person
 * types, and a stale figure there would make the form argue with the server.
 */
export function useBirdsAlive(flockId: string) {
  return useQuery({
    queryKey: ['sd', 'birds-alive', flockId],
    queryFn: () => api.get('/api/v1/sd/dispatches/birds-alive', { flockId }),
    enabled: Boolean(flockId),
    staleTime: 0,
  });
}

/** The suggestion for the paper book's next serial. A guess, never a reservation. */
export function useNextBookNo(farmId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['sd', 'next-book-no', farmId],
    queryFn: () => api.get('/api/v1/sd/dispatches/next-book-no', { farmId }),
    enabled: enabled && Boolean(farmId),
    staleTime: 0,
  });
}

export function useBuyers(options: { search?: string; kind?: string } = {}) {
  return useQuery({
    queryKey: ['fin', 'buyers', options],
    queryFn: () =>
      api.get('/api/v1/fin/buyers', {
        ...(options.search ? { search: options.search } : {}),
        ...(options.kind ? { kind: options.kind } : {}),
        take: 200,
      }) as Promise<Paged<BuyerRow>>,
  });
}

export function useLiveFlocks(farmIds: readonly string[]) {
  return useQuery({
    queryKey: ['flocks', 'live', farmIds],
    queryFn: () =>
      api.get('/api/v1/flocks', {
        ...farmScopeQuery(farmIds),
        status: 'live',
        take: 200,
      }),
  });
}

export function useCashBoxes(farmIds: readonly string[]) {
  return useQuery({
    queryKey: ['fin', 'cash-boxes', farmIds],
    queryFn: () =>
      api.get('/api/v1/fin/cash-boxes', farmScopeQuery(farmIds)) as Promise<{
        items: CashBoxRow[];
      }>,
  });
}

export interface OtherSaleFilters {
  farmIds: readonly string[];
  kind?: string;
  status?: string;
  from?: string;
  to?: string;
  take: number;
  skip: number;
}

export function useOtherSales(filters: OtherSaleFilters) {
  return useQuery({
    queryKey: ['sd', 'other-sales', filters],
    queryFn: () =>
      api.get('/api/v1/sd/other-sales', {
        ...farmScopeQuery(filters.farmIds),
        ...(filters.kind ? { kind: filters.kind } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.from ? { from: filters.from } : {}),
        ...(filters.to ? { to: filters.to } : {}),
        take: filters.take,
        skip: filters.skip,
      }) as Promise<Paged<OtherSaleRow>>,
  });
}

/**
 * One mutation hook for everything a dispatch screen does.
 *
 * The caller passes a function rather than a URL, so a screen reads as the thing
 * it is doing — `run(() => save(draft))` — and every one of them invalidates the
 * same caches on the way out.
 */
export function useSalesMutation<T>() {
  const invalidate = useSalesInvalidation();
  return useMutation<T, Error, () => Promise<T>>({
    mutationFn: (action) => action(),
    onSuccess: invalidate,
  });
}

export const SALE_KIND_LABELS: Record<string, string> = {
  EMPTY_BAG: 'Empty bags',
  MANURE: 'Manure',
  SCRAP_OTHER: 'Scrap and other',
};

export const BUYER_KIND_LABELS: Record<string, string> = {
  BROKER: 'Broker',
  CUSTOMER: 'Shop or customer',
  WALK_IN: 'Walk-in, cash',
};
